import { Injectable } from '@angular/core';
import { Cra, CraNote, CraNoteType } from '../models/CRA';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SearchPackage } from '../packages-module/models/searchPackage';
import { UserLight } from '../users-module/models/userLight';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { ObjectUpdatePayload } from '../models/screeningInvite';
import { environment } from 'src/environments/environment';
import { FilteringService } from './filtering.service';
import { Address } from '../models/Address';
import { CraBillingOptions } from '../models/CRABillingOptions';
import { Client } from '../clients-module/models/clientLocation';
import { AddressType } from '../models/Address';
import * as moment from 'moment';

export enum craEditMode {
    list = 0,
    view = 1,
    add = 2,
    edit = 3
}

@Injectable({
    providedIn: 'root'
})
export class CraService {
    cras: Array<Cra>;
    craUsers: Array<UserLight>;
    craClients: Array<Client>;
    currentCra: Cra;
    currentCraBillingOptions = new CraBillingOptions();
    currentCraPackages: Array<SearchPackage>;
    loggedInUserPackages: Array<SearchPackage>; // FOR a CRA that is logged in so we can track their pkgs and the cra in question.
    craLoaded = new Subject<boolean>();
    crasLoaded = new Subject<boolean>();
    billingOptionsLoaded = new Subject<boolean>();
    addressesLoaded = new Subject<boolean>();
    inCraSetup: boolean;
    onlyActiveCras: boolean;
    craEditMode: craEditMode;
    pliableAddr: Address;

    stateArray: Array<string> = [
        'AL',
        'AK',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DC',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MD',
        'MA',
        'MI',
        'MN',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY'
    ];
    craNoteTypes: CraNoteType[] = [
        new CraNoteType(1, 'Credentialing', 'changes'),
        new CraNoteType(2, 'Account Setup', 'setup'),
        new CraNoteType(3, 'Check-in', 'checkin'),
        new CraNoteType(4, 'Past Due', 'pastdue'),
        new CraNoteType(5, 'Payment', 'payment'),
        new CraNoteType(6, 'Permanent Disable', 'disabled'),
        new CraNoteType(7, 'Question / Problem', 'preguntas'),
        new CraNoteType(8, 'Rerun', 'rerun'),
        new CraNoteType(9, 'Refund', 'refund'),
        new CraNoteType(10, 'Review / Audit', 'review')
    ];

    constructor(
        private http: HttpClient,
        public toast: ToastrService,
        private auth: AuthService
    ) {
        this.auth.userFetchComplete.subscribe(e => {
            if (this.auth.isAuthenticated) {

            }
        });
    }

    get keys(): Array<string> {
        const keys = Object.keys(CraNoteType);
        return keys;
    }

    public SetActiveCra(cra: Cra): void {
        // set this cra as the active cra and fetch their locations.
        this.currentCra = cra;

        this.FetchCraClients(this.currentCra.id);
        this.FetchCraUsers(this.currentCra.id);
        this.FetchCraBillingOptions(this.currentCra.id);
        this.FetchCraAddresses(this.currentCra.id);

        // this.LoadCraNotes();

        this.FetchCraSearchPackages(this.currentCra.id).subscribe(
            (results: Array<SearchPackage>) => {
                if (results) {
                    this.currentCraPackages = results.map(
                        v => {
                            v.searches = v.searches;
                            return v;
                        },
                        error => {
                        }
                    );
                }
            }
        );

        this.craLoaded.next(true);
    }

    private getCraAddress(addresses: Address[], addressType: AddressType): Address {

        let address: Address;

        if (addresses.length > 0) {
            address = addresses.find(a => a.addressType === addressType);
        }

        if (address) {
            return address;
        }

        address = new Address();
        address.id = 0;
        address.addressType = addressType;
        address.street = '';
        address.street2 = '';
        address.city = '';
        address.region = '';
        address.postalCode = null;

        return address;
    }

    public FetchCraBillingOptions(id: number): void {
        this.http.get<CraBillingOptions>(environment.baseURl + `/api/cra/billing/options/` + id)
            .subscribe((bo: CraBillingOptions) => {
                this.currentCra.craBillingOptions = bo;
                this.billingOptionsLoaded.next(true);
            },
                (err: any) => {
                    this.toast.error(
                        `There was an error fetching billing options for cra ${
                        this.currentCra.craName
                        }`,
                        'BillingOptions Fetch Error',
                        { disableTimeOut: true, closeButton: true }
                    );
                }
            );
    }

    public FetchCraUsers(id: number): void {
        this.http
            .get<Array<UserLight>>(environment.baseURl + `/api/cra/users/` + id)
            .subscribe(
                (scc: Array<UserLight>) => {
                    this.craUsers = scc;
                },
                (err: any) => {
                    this.toast.error(
                        `There was an error fetching users for cra ${
                        this.currentCra.craName
                        }`,
                        'Users Fetch Error',
                        { disableTimeOut: true, closeButton: true }
                    );
                }
            );
    }

    public FetchCraAddresses(id: number): void {
        this.http.get<Array<Address>>(environment.baseURl + `/api/cra/addresses/` + id).subscribe((addresses: Address[]) => {

            this.currentCra.craBillingAddress = this.getCraAddress(addresses, AddressType.Billing);
            this.currentCra.craDisplayedOnInvoicesAddress = this.getCraAddress(addresses, AddressType.DisplayOnInvoices);
            this.currentCra.craDisplayedToClientsAddress = this.getCraAddress(addresses, AddressType.DisplayToClients);
            this.currentCra.craPhysicalAddress = this.getCraAddress(addresses, AddressType.Physical);
            this.currentCra.craDisplayedOnReportsAndFormsAddress = this.getCraAddress(addresses, AddressType.DisplayOnReportsAndForms);

            this.addressesLoaded.next(true);
        },
            (err: any) => {
                this.toast.error(
                    `There was an error fetching addresses for cra ${
                    this.currentCra.craName
                    }`,
                    'Addresses Fetch Error',
                    { disableTimeOut: true, closeButton: true }
                );
            }
        );
    }

    public FetchCraClients(id: number): void {
        this.http
            .get<Array<Client>>(environment.baseURl + `/api/cra/clients/` + id)
            .subscribe(
                (scc: Array<Client>) => {
                    this.craClients = scc;
                },
                (err: any) => {
                    this.toast.error(
                        `There was an error fetching clients for cra ${
                        this.currentCra.craName
                        }`,
                        'Clients Fetch Error',
                        { disableTimeOut: true, closeButton: true }
                    );
                }
            );
    }

    FetchCraSearchPackages(id: number): Observable<Array<SearchPackage>> {
        return this.http.get<Array<SearchPackage>>(
            environment.baseURl + `/api/cra/packages/` + id
        );
    }

    formatAsUTCLocal(date: Date, showTime: boolean): string {
        if (date === undefined || date == null) { return null; }
        const m = moment.utc(date);
        if (!m.isValid()) { return null; }
        if (showTime) { return m.local().format('M/D/YYYY h:mm A'); }
        return m.local().format('M/D/YYYY');
    }

    GetSearchTypes(): Observable<Object> {
        return this.http.get(environment.baseURl + `/api/search/searches`);
    }

    SaveSearchPackage(pkg: SearchPackage) {
        return this.http.post(environment.baseURl + '/api/search/packages', pkg);
    }

    AddCraAddress(pkg: Address) {
        return this.http.post<number>(
            environment.baseURl + '/api/cra/address/add',
            pkg
        );
    }

    AssociatePackageToCra(pkgId: number): Observable<Object> {
        return this.http.get(
            environment.baseURl +
            `/api/search/package/associate/${this.currentCra.id}/${pkgId}`
        );
    }

    RemovePackageFromCra(pkgId: number): Observable<Object> {
        return this.http.get(
            environment.baseURl +
            `/api/search/package/disassociate/${this.currentCra.id}/${pkgId}`
        );
    }

    public SaveCra(cra: any): Observable<any> {
        return this.http.post<any>(environment.baseURl + '/api/cra/save', cra);
    }

    SaveCraBillingOptions(cra: Cra): Observable<Object> {
        // TODO: Add toaster msgs
        return this.http.post(environment.baseURl + '/api/cra/billing/save', cra);
    }

    UpdateCra(cra: Cra) {
        this.http.post(environment.baseURl + '/api/cra/update', cra).subscribe(
            (rsp: boolean) => {
                if (rsp) {
                    this.toast.success('Cra was updated successfully.', 'Cra Updated!');
                } else {
                    this.toast.warning('Cra update failed.', 'Update Failed!', {
                        disableTimeOut: true,
                        closeButton: true
                    });
                }
            },
            () => {
                this.toast.warning('Cra update failed.', 'Update Failed!', {
                    disableTimeOut: true,
                    closeButton: true
                });
            }
        );
    }

    UpdateCraBillingOptions(craBo: CraBillingOptions) {
        this.http
            .post(environment.baseURl + '/api/cra/billing/update', craBo)
            .subscribe(
                (rsp: boolean) => {
                    if (rsp) {
                        this.toast.success(
                            'Cra was updated successfully.',
                            'Cra Billing Options Updated!'
                        );
                    } else {
                        this.toast.warning(
                            'Cra update failed.',
                            'Billing Options Update Failed!',
                            { disableTimeOut: true, closeButton: true }
                        );
                    }
                },
                () => {
                    this.toast.warning(
                        'Cra update failed.',
                        'Billing Options Update Failed!',
                        { disableTimeOut: true, closeButton: true }
                    );
                }
            );
    }

    UpdateCraSingle(updates: Array<ObjectUpdatePayload>): Observable<any> {
        return this.http.post<any>(environment.baseURl + `/api/cra/update`, updates);
    }

    UpdateCraBiilingOptionsSingle(updates: Array<ObjectUpdatePayload>) {
        return this.http.post(
            environment.baseURl + `/api/cra/billing/update`,
            updates
        );
    }

    UpdatePropertySingle(updates: Array<ObjectUpdatePayload>) {
        return this.http.post(environment.baseURl + `/api/cra/loc/update`, updates);
    }

    getNoteType(val: number) {
        try {
            return this.craNoteTypes.find(t => {
                return t.id === val;
            }).className;
        } catch (error) {
        }
    }
}
